/**
 * The Stears type scale, measured from the Stears Pro Figma file (node 4352:18988,
 * "Portfolio Reporting"). The Figma is the source of truth: it agrees with the
 * Vector 2.0 design system file and not with the larger scale used on the public
 * stears.co article pages, which is set for long-form reading rather than for a
 * dense data-entry surface.
 *
 * Two families. Plus Jakarta Sans for headings, tabs, buttons and data; Mulish for
 * field labels and input text.
 *
 *   page title        Mulish            700  20 / 28
 *   panel heading     Plus Jakarta Sans 600  16 / 24
 *   section heading   Plus Jakarta Sans 500  14 / 22
 *   tab label         Plus Jakarta Sans 400  14 / 22
 *   body / meta       Plus Jakarta Sans 400  12 / 16
 *   field label       Mulish            400  12 / 15
 *   input value       Mulish            400  12 / 16
 *   sidebar eyebrow   Plus Jakarta Sans 400  10 / 12
 */
export interface TypeStyle {
  fontSize: number
  lineHeight: string
}

const style = (fontSize: number, lineHeight: number): TypeStyle => ({
  fontSize,
  lineHeight: `${lineHeight}px`,
})

export const type = {
  /** Page title, e.g. "Portfolio Reporting". Mulish 700. */
  pageTitle: style(20, 28),
  /** Panel heading inside a card, e.g. "File Format Requirements". Plus Jakarta 600. */
  panelHeading: style(16, 24),
  /** Section heading within a form, e.g. "Reporting period". Plus Jakarta 500. */
  sectionHeading: style(14, 22),
  /** Tab labels. Plus Jakarta 400. */
  tab: style(14, 22),
  /** Body copy, table cells, button labels, helper lines. Plus Jakarta 400. */
  body: style(12, 16),
  /** Field labels above an input. Mulish 400. */
  label: style(12, 15),
  /** Text inside an input or select. Mulish 400. */
  input: style(12, 16),
  /** Sidebar section eyebrow and the smallest legal text. */
  eyebrow: style(10, 12),
} as const satisfies Record<string, TypeStyle>

/** Colours the form design uses for text, measured from the same Figma frames. */
export const textColor = {
  /** Primary text and active tab. */
  primary: '#FFFFFF',
  /** Field labels. */
  label: '#E5E7EB',
  /** Inactive tabs, placeholders, sidebar items. */
  muted: '#9CA3AF',
  /** Helper and example lines. */
  faint: '#6B7280',
  /** The required-field asterisk. */
  required: '#E0645B',
} as const

/** Surfaces the form design uses, measured from the same Figma frames. */
export const surface = {
  /** Page and shell background. */
  page: '#1A1D21',
  /** Panels: title bar, tab bar, form body. */
  panel: '#0E0F10',
  /** Callout inside a panel, e.g. the file-format requirements box. */
  callout: '#1A1B1E',
  /** Input fill. */
  input: '#1F2937',
  /** Select affordance and icon buttons. */
  control: '#4B5563',
  /** Hairline borders. */
  border: '#4B5563',
} as const

export type TypeToken = keyof typeof type
